// client/src/coms/components/BroadcastPanel.js
// "Go live" to YouTube / Twitch / custom RTMP through the server (LiveKit Egress), plus the LIVE banner
// every participant of the voice channel sees. Stream keys are typed here, sent once to the server and
// cleared from memory; they are never stored in the browser or shown again.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useVoiceRoomPermissions } from '../usePermissions';
import { comsApiDelete, comsApiGet, comsApiPost } from '../comsApi';
import { ensureScreenShareOnLiveKit } from '../voip/p2pScreenShare';
import { logicalRoomName } from '../voip/voiceUtils';

function errorText(e, fallback) {
  return String((e && e.data && e.data.error) || fallback);
}

const fieldStyle = {
  background: 'var(--vxs-1, #111214)',
  color: 'var(--vx-text, #fff)',
  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
  borderRadius: 8,
  padding: '8px 10px',
  width: '100%',
  boxSizing: 'border-box',
};

export function useRoomBroadcasts(room) {
  const roomName = logicalRoomName(room && room.name);
  const [broadcasts, setBroadcasts] = useState([]);
  const [endedNotice, setEndedNotice] = useState(null);
  const prevRef = useRef([]);

  const refresh = useCallback(async () => {
    if (!roomName) {
      setBroadcasts([]);
      prevRef.current = [];
      return;
    }
    try {
      const r = await comsApiGet(`/broadcast/active?room=${encodeURIComponent(roomName)}`);
      const next = Array.isArray(r && r.broadcasts) ? r.broadcasts : [];
      // A broadcast that disappeared: if it was mine, say how it ended (failed key, permission removed,
      // screen share stopped, stopped by a moderator). The server only returns my own in /mine.
      const gone = prevRef.current.filter((b) => !next.some((n) => n.id === b.id)).map((b) => b.id);
      prevRef.current = next;
      setBroadcasts(next);
      if (gone.length) {
        const mine = await comsApiGet('/broadcast/mine').catch(() => null);
        const hit = (mine && Array.isArray(mine.broadcasts) ? mine.broadcasts : []).find((b) => gone.includes(b.id));
        if (hit && (hit.status === 'failed' || hit.error)) setEndedNotice(hit);
      }
    } catch {
      setBroadcasts([]);
    }
  }, [roomName]);

  useEffect(() => {
    refresh();
    if (!roomName) return undefined;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [refresh, roomName]);

  return { broadcasts, refresh, endedNotice, dismissEnded: () => setEndedNotice(null) };
}

export function BroadcastEndedNotice({ notice, onDismiss }) {
  if (!notice) return null;
  return (
    <div role="alert" className="coms-form-error" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ minWidth: 0 }}>
        {notice.status === 'failed' ? 'Your broadcast failed' : 'Your broadcast ended'}
        {notice.error ? `: ${notice.error}` : '.'}
      </span>
      <button type="button" className="coms-mini-btn tiny" style={{ marginLeft: 'auto' }} onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

// Visible to everyone in the channel: an external broadcast is clearly different from a private share.
export function LiveBroadcastBanner({ broadcasts, nameForUserId, myUserId, onStop }) {
  const list = Array.isArray(broadcasts) ? broadcasts : [];
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
      {list.map((b) => {
        const who = String(b.user_id) === String(myUserId) ? 'You are' : `${(nameForUserId && nameForUserId(b.user_id)) || 'Someone'} is`;
        const where = (b.targets || []).map((t) => t.platform_label).join(', ') || 'an external service';
        return (
          <div
            key={b.id}
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 10,
              background: 'rgba(220,38,38,0.16)',
              border: '1px solid rgba(248,113,113,0.55)',
              color: 'var(--vx-text, #fff)',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span style={{ background: '#dc2626', borderRadius: 6, padding: '2px 8px', fontWeight: 900, letterSpacing: '0.04em' }}>
              {b.status === 'live' ? 'LIVE' : b.status === 'ending' ? 'ENDING' : 'CONNECTING'}
            </span>
            <span style={{ minWidth: 0 }}>
              {who} broadcasting their screen publicly to {where}
              {b.title ? ` — “${b.title}”` : ''}. Voice chat is not included.
            </span>
            {typeof onStop === 'function' && String(b.user_id) === String(myUserId) ? (
              <button type="button" className="coms-mini-btn" style={{ marginLeft: 'auto' }} onClick={() => onStop(b.id)}>
                Stop broadcast
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default function BroadcastPanel({ room, open, onClose, screenSharing, onStarted }) {
  // Destinations offered follow the platform permissions in this voice channel (enforced by the server).
  const voicePerms = useVoiceRoomPermissions(room && room.name);
  const [status, setStatus] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [selected, setSelected] = useState({});
  const [title, setTitle] = useState('');
  const [consent, setConsent] = useState(false);
  const [includeMic, setIncludeMic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ platform: 'youtube', label: '', ingest_url: '', stream_key: '', save: true });

  const load = useCallback(async () => {
    setErr('');
    try {
      const [s, d] = await Promise.all([comsApiGet('/broadcast/status'), comsApiGet('/broadcast/destinations')]);
      setStatus(s);
      setDestinations(Array.isArray(d && d.destinations) ? d.destinations : []);
    } catch (e) {
      setErr(errorText(e, 'Could not load broadcast settings.'));
    }
  }, []);

  useEffect(() => {
    if (open) load();
    if (!open) {
      setConsent(false);
      setForm((f) => ({ ...f, stream_key: '' }));
    }
  }, [open, load]);

  if (!open) return null;

  const PLATFORM_PERMISSION = { youtube: 'broadcast_youtube', twitch: 'broadcast_twitch', custom: 'broadcast_custom' };
  const platforms = ((status && status.platforms) || []).filter((p) => voicePerms.has(PLATFORM_PERMISSION[p.key] || 'broadcast_custom'));
  const canSave = !!(status && status.can_save_destinations);
  const available = !!(status && status.egress_available);
  const chosenIds = Object.keys(selected).filter((k) => selected[k]).map(Number);
  const manualReady = !!form.stream_key.trim() && (!canSave || !form.save);

  async function saveDestination() {
    setBusy(true);
    setErr('');
    try {
      const r = await comsApiPost('/broadcast/destinations', {
        platform: form.platform,
        label: form.label,
        ingest_url: form.ingest_url,
        stream_key: form.stream_key,
      });
      setForm((f) => ({ ...f, stream_key: '', label: '', ingest_url: '' }));
      if (r && r.destination) {
        setDestinations((list) => [...list, r.destination]);
        setSelected((sel) => ({ ...sel, [r.destination.id]: true }));
      }
    } catch (e) {
      setErr(errorText(e, 'Could not save the destination.'));
    } finally {
      setBusy(false);
    }
  }

  async function removeDestination(id) {
    try {
      await comsApiDelete(`/broadcast/destinations/${id}`);
      setDestinations((list) => list.filter((d) => d.id !== id));
      setSelected((sel) => ({ ...sel, [id]: false }));
    } catch (e) {
      setErr(errorText(e, 'Could not remove the destination.'));
    }
  }

  async function start() {
    if (!room) return;
    setBusy(true);
    setErr('');
    try {
      // Egress reads the SFU copy of the share.
      const moved = await ensureScreenShareOnLiveKit(room);
      if (!moved || !moved.ok) throw Object.assign(new Error(''), { data: { error: 'Start sharing your screen first.' } });

      const manual = manualReady
        ? [{ platform: form.platform, label: form.label, ingest_url: form.ingest_url, stream_key: form.stream_key }]
        : [];
      const r = await comsApiPost('/broadcast/start', {
        room: logicalRoomName(room.name),
        title,
        consent: true,
        include_microphone: includeMic,
        destination_ids: chosenIds,
        manual,
      });
      setForm((f) => ({ ...f, stream_key: '' }));
      if (typeof onStarted === 'function') onStarted(r && r.broadcast);
      onClose();
    } catch (e) {
      setErr(errorText(e, 'Could not start the broadcast.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Broadcast to a streaming platform"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflow: 'auto', background: 'var(--vxs-3, #1b1c1f)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 14, padding: 18, color: 'var(--vx-text, #fff)' }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Go live</div>
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>Send your screen share to YouTube, Twitch or another RTMP service. Viewers here keep watching as before.</div>

        {status && !available ? (
          <div className="coms-form-error" style={{ marginTop: 12 }}>
            {status.reason === 'voice_not_configured'
              ? 'Voice is not available on this server.'
              : 'Broadcasting is not available yet: the server administrator needs to deploy the LiveKit Egress service.'}
          </div>
        ) : null}

        {!screenSharing ? (
          <div style={{ marginTop: 12, fontSize: 13, color: 'rgba(255,200,120,0.95)' }}>Start sharing your screen in this voice channel first.</div>
        ) : null}

        <div style={{ marginTop: 14, fontWeight: 800, fontSize: 13 }}>Destinations</div>
        {destinations.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {destinations.map((d) => (
              <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={!!selected[d.id]} onChange={(e) => setSelected((sel) => ({ ...sel, [d.id]: e.target.checked }))} />
                <span>
                  {d.label || d.platform_label} <span style={{ opacity: 0.6 }}>({d.platform_label}, {d.ingest_host}, key {d.key_hint})</span>
                </span>
                <button type="button" className="coms-mini-btn tiny" style={{ marginLeft: 'auto' }} onClick={() => removeDestination(d.id)}>
                  Remove
                </button>
              </label>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>No saved destinations.</div>
        )}

        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))', display: 'grid', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} style={fieldStyle} aria-label="Platform">
              {(platforms.length ? platforms : [{ key: 'youtube', label: 'YouTube Live' }, { key: 'twitch', label: 'Twitch' }, { key: 'custom', label: 'Custom RTMP' }]).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
            <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Name (optional)" style={fieldStyle} maxLength={60} />
          </div>
          <input
            value={form.ingest_url}
            onChange={(e) => setForm((f) => ({ ...f, ingest_url: e.target.value }))}
            placeholder={(platforms.find((p) => p.key === form.platform) || {}).default_ingest || 'rtmps://server/app'}
            style={fieldStyle}
            aria-label="Server URL"
          />
          <input
            type="password"
            autoComplete="off"
            value={form.stream_key}
            onChange={(e) => setForm((f) => ({ ...f, stream_key: e.target.value }))}
            placeholder="Stream key"
            style={fieldStyle}
            aria-label="Stream key"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            {canSave ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.save} onChange={(e) => setForm((f) => ({ ...f, save: e.target.checked }))} />
                Save this destination (key stored encrypted on the server)
              </label>
            ) : (
              <span style={{ opacity: 0.7 }}>This key is used for this broadcast only.</span>
            )}
            {canSave && form.save ? (
              <button type="button" className="coms-mini-btn tiny" style={{ marginLeft: 'auto' }} disabled={busy || !form.stream_key.trim()} onClick={saveDestination}>
                Save
              </button>
            ) : null}
          </div>
        </div>

        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Broadcast title (shown to your voice channel)" style={{ ...fieldStyle, marginTop: 12 }} maxLength={120} />

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 13, lineHeight: 1.4 }}>
          <input type="checkbox" checked={includeMic} onChange={(e) => setIncludeMic(e.target.checked)} />
          <span>
            Include my microphone (instead of the screen's own audio), so I can talk over my screen.
          </span>
        </label>

        {status && status.output ? (
          <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.45, opacity: 0.8 }} aria-live="polite">
            Output: {status.output.label}, about {(status.output.kbps_per_destination / 1000).toFixed(1)} Mbps sent by the voice server to
            each destination
            {chosenIds.length + (manualReady ? 1 : 0) > 1
              ? ` (${(((chosenIds.length + (manualReady ? 1 : 0)) * status.output.kbps_per_destination) / 1000).toFixed(1)} Mbps for ${chosenIds.length + (manualReady ? 1 : 0)} destinations)`
              : ''}
            . Your own upload does not grow: you send one copy of your screen to the voice server. Up to {status.max_destinations || 3} destinations
            per broadcast; this installation runs {status.installation_limit === 1 ? 'one broadcast' : `${status.installation_limit} broadcasts`} at a time.
          </div>
        ) : null}

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 13, lineHeight: 1.4 }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>
            I understand that my <b>screen and {includeMic ? 'my microphone' : 'its audio'}</b> will be broadcast <b>publicly</b> to the selected services. Other participants' voices and cameras are not included.
          </span>
        </label>

        {err ? (
          <div className="coms-form-error" style={{ marginTop: 10 }}>
            {err}
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="coms-mini-btn primary"
            disabled={busy || !available || !screenSharing || !consent || (!chosenIds.length && !manualReady)}
            onClick={start}
          >
            {busy ? 'Starting…' : 'Start broadcast'}
          </button>
        </div>
      </div>
    </div>
  );
}

export async function stopBroadcast(id) {
  return comsApiPost(`/broadcast/${encodeURIComponent(id)}/stop`, {});
}
